import { useMemo, useState } from 'react';

const MAX_SUGGESTIONS = 10;

const s = {
  wrap:      { position: 'relative', flex: '1 1 280px' },
  input:     { width: '100%', padding: '12px 14px', fontSize: 17, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff', outline: 'none' },
  list:      { position: 'absolute', top: 'calc(100% + 4px)', insetInline: 0, zIndex: 10, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #d0d7de', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 360, overflowY: 'auto' },
  item:      { display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px', borderRadius: 6, cursor: 'pointer' },
  active:    { background: '#f0f3f6' },
  typeLabel: { fontSize: 12, color: '#8c959f', flexShrink: 0 },
};

// התאמה כמו ב-spSearchProducts: כל מילה בחיפוש צריכה להופיע בטקסט.
// דירוג: הטקסט מתחיל בחיפוש → מילה בטקסט מתחילה במילה הראשונה → השאר; ובתוך כל רמה — הקצר קודם.
const findSuggestions = (items, query) => {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const words = q.split(/\s+/);
  const matches = [];
  for (const item of items) {
    const text = item.lower;
    if (!words.every((w) => text.includes(w))) continue;
    const rank = text.startsWith(q) ? 0 : (` ${text}`.includes(` ${words[0]}`) ? 1 : 2);
    matches.push({ item, rank });
  }
  matches.sort((a, b) => a.rank - b.rank || a.item.text.length - b.item.text.length);
  return matches.slice(0, MAX_SUGGESTIONS).map((m) => m.item);
};

// שדה חיפוש עם השלמה אוטומטית מקומית (בלי קריאות לשרת בזמן הקלדה).
// items: [{ text, lower, type: 'product' | 'brand' }]. onSubmit(text) נקרא ב-Enter או בבחירת הצעה.
const SearchBox = ({ value, onChange, onSubmit, items }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const suggestions = useMemo(() => findSuggestions(items, value), [items, value]);
  const showList = open && suggestions.length > 0;

  const choose = (text) => {
    onChange(text);
    setOpen(false);
    setActiveIndex(-1);
    onSubmit(text);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp' && suggestions.length) {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showList && activeIndex >= 0) choose(suggestions[activeIndex].text);
      else { setOpen(false); onSubmit(value); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div style={s.wrap}>
      <input
        style={s.input}
        type="search"
        autoFocus
        placeholder="חיפוש לפי שם מוצר או חברה"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        role="combobox"
        aria-expanded={showList}
        aria-controls="search-suggestions"
        aria-autocomplete="list"
      />
      {showList && (
        // onMouseDown + preventDefault: הלחיצה לא מורידה את הפוקוס מהשדה לפני שהבחירה נקלטת
        <ul id="search-suggestions" role="listbox" style={s.list} onMouseDown={(e) => e.preventDefault()}>
          {suggestions.map((sug, i) => (
            <li
              key={`${sug.type}:${sug.text}`}
              role="option"
              aria-selected={i === activeIndex}
              style={{ ...s.item, ...(i === activeIndex ? s.active : null) }}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => choose(sug.text)}
            >
              <span>{sug.text}</span>
              {sug.type === 'brand' && <span style={s.typeLabel}>חברה</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
